import { useState } from "react";
import type { Attendee } from "../lib/attendees";

type AvatarProps = {
  attendee: Pick<
    Attendee,
    "guestId" | "firstName" | "lastName" | "fullName" | "photoUrl"
  >;
  size?: number;
  className?: string;
};

const PALETTE = [
  "bg-rose-500",
  "bg-amber-500",
  "bg-emerald-500",
  "bg-cyan-500",
  "bg-sky-500",
  "bg-indigo-500",
  "bg-fuchsia-500",
  "bg-violet-500",
  "bg-teal-500",
  "bg-orange-500",
] as const;

const hash = (s: string): number => {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = (h * 31 + s.charCodeAt(i)) >>> 0;
  }
  return h;
};

const colorFor = (guestId: string): string =>
  PALETTE[hash(guestId) % PALETTE.length];

const initialsFor = (a: AvatarProps["attendee"]): string => {
  const first = a.firstName?.[0] ?? "";
  const last = a.lastName?.[0] ?? "";
  const combined = `${first}${last}`.trim();
  if (combined) return combined.toUpperCase();
  const fallback = a.fullName?.trim().split(/\s+/).slice(0, 2);
  return (fallback?.map((p) => p[0]).join("") ?? "?").toUpperCase();
};

export const Avatar = ({ attendee, size = 48, className }: AvatarProps) => {
  const photoUrl = attendee.photoUrl?.trim() || null;
  const [photoFailed, setPhotoFailed] = useState(false);

  const baseClasses =
    "inline-flex items-center justify-center overflow-hidden rounded-full ring-2 ring-white/10 shadow-sm";
  const sizeStyle = { width: size, height: size };

  if (photoUrl && !photoFailed) {
    return (
      <div
        className={`${baseClasses} bg-slate-800 ${className ?? ""}`}
        style={sizeStyle}
        aria-label={attendee.fullName}
        title={attendee.fullName}
      >
        <img
          src={photoUrl}
          alt={attendee.fullName}
          width={size}
          height={size}
          loading="lazy"
          decoding="async"
          onError={() => setPhotoFailed(true)}
          className="h-full w-full object-cover"
        />
      </div>
    );
  }

  const bg = colorFor(attendee.guestId);
  const initials = initialsFor(attendee);

  return (
    <div
      className={`${baseClasses} font-semibold text-white ${bg} ${className ?? ""}`}
      style={{
        ...sizeStyle,
        fontSize: Math.max(12, Math.round(size * 0.4)),
      }}
      aria-label={attendee.fullName}
      title={attendee.fullName}
    >
      {initials}
    </div>
  );
};
